import React, { useState, useEffect, useContext } from "react";
import classes from "./Trucks.module.css";
import { Row } from "../Shared/FlexDiv/FlexDiv";
import { Activity, currentRequestTimeStamp, isMobile } from "../../Constants";
import ReactGA from "react-ga";
import { Urls } from "../../Urls";
import mixpanel from "mixpanel-browser";
import RouterOutlet from "./TrucksRoute";
import { Link } from "react-router-dom";
import { useMixPanelForTracking } from "../../utils/TrackMixPanel";
import { UserApiContext } from "../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../MyCompanyProfileContext";
import useViewOnlyAccess from "../../hooks/useViewOnlyAccess";
import Unauthorized from "../Unauthorized";
import { Redirect } from "react-router-dom";


/**
 * TrucksNew component manages the display of different sections (Dashboard, My Enquiries, Transporter Enquiries)
 * within the Trucks module. It also integrates with Google Analytics and Mixpanel for tracking user interactions.
 *
 * @component
 * @param {Object} props - The component props.
 * @returns {React.ReactElement} The TrucksNew component, including navigation and routing.
 */
const TrucksNew = (props) => {
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showTransporter, setShowTransporter] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const [showAgentHandoffs, setShowAgentHandoffs] = useState(false);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);
  const { isRestricted, hasModuleAccess, dashboardAccess, noAccess, loading } = useViewOnlyAccess('spot');
  const [userContent, setuserContent] = useState(<></>);

  const isDashboardAllowedByPolicy =
    userDetails?.policy?.adhoc?.allowed_dashboard === "yes";

  /**
   * useEffect hook to track page views and set the appropriate state based on the current URL.
   * Integrates Google Analytics and Mixpanel for tracking.
   *
   * @function
   */
  useEffect(() => {
    ReactGA.pageview("Trucks Available");
    Activity([
      {
        screen: "LoRRI Adhoc",
        module: "Adhoc Dashboard",
        user_activity: "User has viewed Adhoc Dashboard",
        timestamp: currentRequestTimeStamp(),
        "userEmail": userDetails?.email, "userName": userDetails?.user_name, "companyname": userDetails.company_name
          ? userDetails.company_name
          : userDetails.email
            ? userDetails.email.split("@")[1].split(".")[0]
            : "",

      },
    ]);
    // TrackMixPanel({
    //   ModuleName: "lorri spot",
    //   ScreenName: "my enquiries",
    //   Data: `None`,
    //   Definition: "user has viewed my enquiries section",
    // });

    if (window.location.pathname === "/trucks/dashboard") {
      setShowDashboard(true);
      setShowEnquiry(false);
      setShowTransporter(false);
    } else if (window.location.pathname.startsWith("/trucks/enquiries")) {
      setShowEnquiry(true);
      setShowDashboard(false);
      setShowTransporter(false);
    } else if (window.location.pathname === "/trucks/transporterenquiries") {
      setShowTransporter(true);
      setShowDashboard(false);
      setShowEnquiry(false);
      setShowDrafts(false);
    } else if (window.location.pathname === "/trucks/drafts") {
      setShowDrafts(true);
      setShowDashboard(false);
      setShowEnquiry(false);
      setShowTransporter(false);
    } else if (window.location.pathname === "/trucks/agenthandoffs") {
      setShowAgentHandoffs(true);
      setShowDashboard(false);
      setShowEnquiry(false);
      setShowTransporter(false);
      setShowDrafts(false);
    }
  }, [showEnquiry, showDashboard, showTransporter]);

  // Handle unauthorized access
  useEffect(() => {
    if (!loading && hasModuleAccess === false) {
      setuserContent(<Unauthorized
        message="You dont have access for this module"
        showPremiumButton={false}
      />);
    }
  }, [loading, hasModuleAccess]);

  if (!loading && hasModuleAccess === false) {
    return userContent;
  }

  /**
   * Handles the event when the "My Enquiries" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const newenquiryClicked = (e) => {
    setShowEnquiry(true);
    setShowDashboard(false);
    setShowTransporter(false);
    Activity([
      {
        screen: "Trucks Available",
        module: "Transporter Enquiries",
        user_activity: "Transporter Enquiries section visited",
        timestamp: currentRequestTimeStamp(),
        "userEmail": userDetails?.email, "userName": userDetails?.user_name, "companyname": userDetails.company_name
          ? userDetails.company_name
          : userDetails.email
            ? userDetails.email.split("@")[1].split(".")[0]
            : ""
      },
    ]);
    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "transporter enquiries",
      Data: `None`,
      Definition: "user has viewed transporter enquiries section",
    });
  };

  /*
   * Handles the event when the "Transporter Enquiries" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const trucksClicked = (e) => {
    setShowEnquiry(false);
    setShowDashboard(false);
    setShowTransporter(true);
    Activity([
      {
        screen: "Trucks Available",
        module: "My Enquiries",
        user_activity: "My Enquiries section visited",
        timestamp: currentRequestTimeStamp(),
        "userEmail": userDetails?.email, "userName": userDetails?.user_name, "companyname": userDetails.company_name
          ? userDetails.company_name
          : userDetails.email
            ? userDetails.email.split("@")[1].split(".")[0]
            : ""
      },
    ]);
    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "my enquiries",
      Data: `None`,
      Definition: "user has viewed my enquiries section",
    });
  };

  /**
   * Handles the event when the "Draft Enquiries" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const draftsClicked = (e) => {
    setShowEnquiry(false);
    setShowDashboard(false);
    setShowTransporter(false);
    setShowDrafts(true);
    Activity([
      {
        screen: "Trucks Available",
        module: "Draft Enquiries",
        user_activity: "Draft Enquiries section visited",
        timestamp: currentRequestTimeStamp(),
        "userEmail": userDetails?.email, "userName": userDetails?.user_name, "companyname": userDetails.company_name
          ? userDetails.company_name
          : userDetails.email
            ? userDetails.email.split("@")[1].split(".")[0]
            : ""
      },
    ]);
  };

  /**
   * Handles the event when the "Dashboard" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const agentHandoffsClicked = (e) => {
    setShowEnquiry(false);
    setShowDashboard(false);
    setShowTransporter(false);
    setShowDrafts(false);
    setShowAgentHandoffs(true);
  };

  const dashboardClicked = (e) => {
    setShowEnquiry(false);
    setShowDashboard(true);
    setShowTransporter(false);
    Activity([
      {
        screen: "Trucks Available",
        module: "Adhoc Dashboard",
        user_activity: "Adhoc Dashboard section visited",
        timestamp: currentRequestTimeStamp(),
        "userEmail": userDetails?.email, "userName": userDetails?.user_name, "companyname": userDetails.company_name
          ? userDetails.company_name
          : userDetails.email
            ? userDetails.email.split("@")[1].split(".")[0]
            : ""
      },
    ]);
    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "dashboard",
      Data: `None`,
      Definition: "user has viewed spot dashboard section",
    });
  };

  return (
    <>
      <div className={classes["trucksHeaderBar"]}>
        {!isMobile() && (
          <nav className={classes["headerNav"]} id="myTab" role="tablist">
            {isDashboardAllowedByPolicy && (
              <Link
                to={!dashboardAccess ? "#" : { pathname: "/trucks/dashboard" }}
                id="adhocdashboard"
                role="tab"
                aria-selected={showDashboard === true && window.location.pathname === "/trucks/dashboard"}
                className={`${classes["headerNavLink"]} ${showDashboard === true && window.location.pathname === "/trucks/dashboard" ? classes["headerNavLinkActive"] : ""} ${!dashboardAccess ? classes['restricted-link'] : ''}`}
                onClick={(e) => !dashboardAccess ? e.preventDefault() : dashboardClicked(e)}
              >
               Spot Dashboard {!dashboardAccess && <i className="fa fa-lock ms-2" />}
              </Link>
            )}
            <Link
              to={!noAccess ? { pathname: "/trucks/enquiries" } : "#"}
              id="enquiries"
              role="tab"
              aria-selected={showEnquiry === true || window.location.pathname.startsWith("/trucks/enquiries")}
              className={`${classes["headerNavLink"]} ${showEnquiry === true || window.location.pathname.startsWith("/trucks/enquiries") ? classes["headerNavLinkActive"] : ""} ${noAccess ? classes['restricted-link'] : ''}`}
              onClick={(e) => !noAccess && newenquiryClicked(e)}
            >
              My Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
            </Link>
            {companySettingData && companySettingData?.agent_settings?.allowed === "yes" && companySettingData?.agent_settings?.spot_agent?.allowed === "yes" && companySettingData?.agent_settings?.spot_agent?.enabled === "yes" && (
              <Link
                to={!noAccess ? { pathname: "/trucks/drafts" } : "#"}
                id="draftenquiries"
                role="tab"
                aria-selected={showDrafts === true && window.location.pathname === "/trucks/drafts"}
                className={`${classes["headerNavLink"]} ${showDrafts === true && window.location.pathname === "/trucks/drafts" ? classes["headerNavLinkActive"] : ""} ${noAccess ? classes['restricted-link'] : ''}`}
                onClick={(e) => !noAccess && draftsClicked(e)}
              >
                Draft Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
              </Link>
            )}
            <Link
              to={!noAccess ? { pathname: "/trucks/transporterenquiries" } : "#"}
              id="transporterenquiries"
              role="tab"
              aria-selected={showTransporter === true && window.location.pathname === "/trucks/transporterenquiries"}
              className={`${classes["headerNavLink"]} ${showTransporter === true && window.location.pathname === "/trucks/transporterenquiries" ? classes["headerNavLinkActive"] : ""} ${noAccess ? classes['restricted-link'] : ''}`}
              onClick={(e) => !noAccess && trucksClicked(e)}
            >
              Transporter Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
            </Link>
            {/* <Link
              to={!noAccess ? { pathname: "/trucks/agenthandoffs" } : "#"}
              id="agenthandoffs"
              role="tab"
              aria-selected={showAgentHandoffs === true && window.location.pathname === "/trucks/agenthandoffs"}
              className={`${classes["headerNavLink"]} ${showAgentHandoffs === true && window.location.pathname === "/trucks/agenthandoffs" ? classes["headerNavLinkActive"] : ""} ${noAccess ? classes['restricted-link'] : ''}`}
              onClick={(e) => !noAccess && agentHandoffsClicked(e)}
            >
              Agent Handoffs {noAccess && <i className="fa fa-lock ms-2" />}
            </Link> */}
          </nav>
        )}
        {isRestricted && (
          <span className={classes["viewOnlyBadge"]}>
            <i className="fa fa-eye" style={{ marginRight: "6px", fontSize: "13px" }} />
            View Only
          </span>
        )}
      </div>

      {/* Main container for Trucks module with navigation and routing */}
      <div className={`${classes["truckscontainer"]}`}>
        {/* Redirect if dashboard is locked and user is on dashboard */}
        {!dashboardAccess && window.location.pathname === "/trucks/dashboard" && (
          <Redirect to="/trucks/enquiries" />
        )}
        {/* Redirect if dashboard not allowed by policy and user is on dashboard */}
        {!isDashboardAllowedByPolicy && window.location.pathname === "/trucks/dashboard" && (
          <Redirect to="/trucks/enquiries" />
        )}
        {/* Redirect if noAccess (dashboard-only) and user is not on dashboard */}
        {hasModuleAccess && noAccess && !window.location.pathname.startsWith("/trucks/dashboard") && (
          <Redirect to="/trucks/dashboard" />
        )}
        {isMobile() && (
          <div className="row">
            <ul
              className="nav nav-tabs"
              id="myTabMobile"
              role="tablist"
              style={{ paddingTop: "1%" }}
            >
              {isDashboardAllowedByPolicy && (
              <li className="nav-item">
                <div
                  className={`nav-link ${showDashboard === true &&
                    window.location.pathname === "/trucks/dashboard"
                    ? "active"
                    : ""
                    }`}
                >
                  <Link
                    to={!dashboardAccess ? "#" : { pathname: "/trucks/dashboard" }}
                    id="adhocdashboardmobile"
                    className={`btn btn-link ${!dashboardAccess ? classes['restricted-link'] : ''}`}
                    onClick={(e) => !dashboardAccess ? e.preventDefault() : dashboardClicked(e)}
                    style={{
                      color: "#393185",
                      fontSize: "14px",
                      fontWeight: "bolder",
                      padding: "0",
                    }}
                  >
                    Dashboard {!dashboardAccess && <i className="fa fa-lock ms-2" />}
                  </Link>
                </div>
              </li>
              )}
              <li className="nav-item">
                <div
                  className={`nav-link ${showEnquiry === true ||
                    window.location.pathname.startsWith("/trucks/enquiries")
                    ? "active"
                    : ""
                    }`}
                >
                  <Link
                    to={!noAccess ? { pathname: "/trucks/enquiries" } : "#"}
                    id="enquiriesmobile"
                    className={`btn btn-link ${noAccess ? classes['restricted-link'] : ''}`}
                    onClick={(e) => !noAccess && newenquiryClicked(e)}
                    style={{
                      color: "#393185",
                      fontSize: "14px",
                      fontWeight: "bolder",
                      padding: "0",
                    }}
                  >
                    My Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
                  </Link>
                </div>
              </li>
              {/* check if agent settings is enabled and agent mode is set to draft */}
              {
                companySettingData && companySettingData?.agent_settings?.allowed === "yes" && companySettingData?.agent_settings?.spot_agent?.allowed === "yes" && companySettingData?.agent_settings?.spot_agent?.enabled === "yes" &&
                < li className="nav-item">
                  <div
                    className={`nav-link ${showDrafts === true &&
                      window.location.pathname === "/trucks/drafts"
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      to={!noAccess ? { pathname: "/trucks/drafts" } : "#"}
                      id="draftenquiriesmobile"
                      className={`btn btn-link ${noAccess ? classes['restricted-link'] : ''}`}
                      onClick={(e) => !noAccess && draftsClicked(e)}
                      style={{
                        color: "#393185",
                        fontSize: "14px",
                        fontWeight: "bolder",
                        padding: "0",
                      }}
                    >
                      Draft Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
                    </Link>
                  </div>
                </li>
              }
              <li className="nav-item">
                <div
                  className={`nav-link ${showTransporter === true &&
                    window.location.pathname === "/trucks/transporterenquiries"
                    ? "active"
                    : ""
                    }`}
                >
                  <Link
                    to={!noAccess ? { pathname: "/trucks/transporterenquiries" } : "#"}
                    id="transporterenquiriesmobile"
                    className={`btn btn-link ${noAccess ? classes['restricted-link'] : ''}`}
                    onClick={(e) => !noAccess && trucksClicked(e)}
                    style={{
                      color: "#393185",
                      fontSize: "14px",
                      fontWeight: "bolder",
                      padding: "0",
                    }}
                  >
                    Transporter Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
                  </Link>
                </div>
              </li>
              <li className="nav-item">
                <div
                  className={`nav-link ${showAgentHandoffs === true &&
                    window.location.pathname === "/trucks/agenthandoffs"
                    ? "active"
                    : ""
                    }`}
                >
                  {/* <Link
                    to={!noAccess ? { pathname: "/trucks/agenthandoffs" } : "#"}
                    id="agenthandoffsmobile"
                    className={`btn btn-link ${noAccess ? classes['restricted-link'] : ''}`}
                    onClick={(e) => !noAccess && agentHandoffsClicked(e)}
                    style={{
                      color: "#393185",
                      fontSize: "14px",
                      fontWeight: "bolder",
                      padding: "0",
                    }}
                  >
                    Agent Handoffs {noAccess && <i className="fa fa-lock ms-2" />}
                  </Link> */}
                </div>
              </li>
            </ul>
          </div>
        )}
        <div className="d-flex flex-column" style={{padding : "1% 3%"}}>
            <RouterOutlet />
        </div>
      </div >
    </>
  );
};

export default TrucksNew;
